import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChatCircleDots, X, PaperPlaneRight, Sparkle, CircleNotch,
  CheckCircle, ShieldCheck, SignOut, Lock, EnvelopeSimple, Lightning,
} from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import SofiaRobot from "./SofiaRobot";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = "Af0D0oyPe14sI3qM7EAAWR3SoK4H4dksIXbLCN1DI5xoaNeAD8qmR16-cVbkeWIvbnFqFl_966ix-RFq";

const STORAGE_KEY = "commandix_sofia_session";

/**
 * Sofia v2 — Floating sales + authenticated assistant.
 *
 * Modes returned by the backend:
 *   anon           – sales pitch
 *   awaiting_email – ask for the customer email
 *   awaiting_pin   – render a 6-digit PIN input
 *   authenticated  – personal assistant; shows user name + plan in header
 *   locked         – soft-blocked due to bad PINs
 *
 * Visual: real-chat feel, big avatar overflowing the panel top, very rounded
 * corners, pill bubbles, deterministic auth UI replacing free-text input
 * during PIN entry.
 */
export default function SofiaChatWidget() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith("en") ? "en" : "es";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text, ts}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("anon");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [authedUser, setAuthedUser] = useState(null); // {name, business_name, plan_name, status}
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [pinDigits, setPinDigits] = useState(["", "", "", "", "", ""]);
  const pinRefs = useRef([]);
  const [upgradeCta, setUpgradeCta] = useState(null); // {type, plan, plan_name, price_usd}
  const [upgradeStatus, setUpgradeStatus] = useState("idle"); // idle|paying|success|error
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Robot avatar state: idle (default), wave (welcome on open), typing (while sending)
  const [botState, setBotState] = useState("idle");
  // Trigger wave each time the chat is opened
  useEffect(() => {
    if (!open) return;
    setBotState("wave");
    const t = setTimeout(() => setBotState("idle"), 1400);
    return () => clearTimeout(t);
  }, [open]);
  // While sending, robot is typing; revert to idle after
  useEffect(() => {
    if (sending) setBotState("typing");
    else if (open) setBotState((s) => (s === "typing" ? "idle" : s));
  }, [sending, open]);

  // Allow other parts of the landing (pricing card, Sofia proposal CTA) to
  // open the chat by dispatching a global event.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-sofia-chat", onOpen);
    return () => window.removeEventListener("open-sofia-chat", onOpen);
  }, []);

  const sessionId = useMemo(() => {
    let s = localStorage.getItem(STORAGE_KEY);
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(STORAGE_KEY, s);
    }
    return s;
  }, []);

  // First-open greeting — identifies user type to choose flow
  useEffect(() => {
    if (open && messages.length === 0) {
      const greet = lang === "en"
        ? "Hi 👋 I'm Sofia, Commandix's assistant. Quick question — do you already have an account, or are you new here?"
        : "¡Hola 👋 Soy Sofia, la asistente de Commandix. Pregunta rápida — ¿ya tienes cuenta o eres nuevo por aquí?";
      setMessages([{ role: "assistant", text: greet, showIntroChips: true }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, mode]);

  // Focus first PIN box when entering awaiting_pin
  useEffect(() => {
    if (mode === "awaiting_pin") {
      setPinDigits(["", "", "", "", "", ""]);
      setTimeout(() => pinRefs.current[0]?.focus(), 200);
    }
  }, [mode]);

  const sendChat = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/saas/sofia/chat`, {
        session_id: sessionId,
        message: msg,
        lang,
      });
      // Parse a <<QUOTE>>...<<END_QUOTE>> block out of the reply — Sofia
      // emits it when she has enough info about the visitor's business.
      // We render it as a structured "quote card" in the chat instead of raw JSON.
      let cleanReply = data.reply;
      let quote = null;
      const m = data.reply && data.reply.match(/<<QUOTE>>([\s\S]*?)<<END_QUOTE>>/);
      if (m) {
        cleanReply = data.reply.slice(0, m.index).trim();
        try {
          quote = JSON.parse(m[1].trim());
        } catch (_e) {
          // If parsing fails, keep the raw text visible (better than silently dropping the quote)
          cleanReply = data.reply;
        }
      }
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: cleanReply },
        ...(quote ? [{ role: "quote", quote }] : []),
      ]);
      if (data.mode) setMode(data.mode);
      if (data.masked_email) setMaskedEmail(data.masked_email);
      if (typeof data.attempts_left === "number") setAttemptsLeft(data.attempts_left);
      if (data.user) setAuthedUser(data.user);
      if (data.mode !== "authenticated") setAuthedUser(null);
      // In-chat upgrade CTA — render a PayPal card inline
      if (data.cta?.type === "upgrade") {
        setUpgradeCta(data.cta);
        setUpgradeStatus("idle");
      }
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: lang === "en"
          ? "Mmm, tiny hiccup. One sec and try again 🙏"
          : "Uy, pequeño tropiezo. Dame un segundo e intenta de nuevo 🙏",
      }]);
    } finally {
      setSending(false);
    }
  };

  const submitPin = async () => {
    const code = pinDigits.join("");
    if (code.length !== 6) return;
    await sendChat(code);
  };

  const handlePinKey = (i, e) => {
    if (e.key === "Backspace" && !pinDigits[i] && i > 0) {
      pinRefs.current[i - 1]?.focus();
    } else if (e.key === "Enter") {
      submitPin();
    }
  };

  const handlePinChange = (i, v) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const next = [...pinDigits];
    next[i] = digit;
    setPinDigits(next);
    if (digit && i < 5) pinRefs.current[i + 1]?.focus();
    if (digit && i === 5 && next.every((d) => d)) {
      // auto-submit when all 6 are filled
      setTimeout(() => sendChat(next.join("")), 80);
    }
  };

  const handlePinPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (txt.length === 6) {
      e.preventDefault();
      const next = txt.split("");
      setPinDigits(next);
      setTimeout(() => sendChat(txt), 80);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/saas/sofia/logout`, { session_id: sessionId });
    } catch { /* ignore */ }
    setMode("anon");
    setAuthedUser(null);
    setMaskedEmail("");
    setAttemptsLeft(null);
    setMessages((prev) => [...prev, {
      role: "assistant",
      text: lang === "en"
        ? "You're back to anonymous mode. Ask me anything ✨"
        : "Listo, modo anónimo otra vez. Pregúntame lo que quieras ✨",
    }]);
  };

  const quickReplies = lang === "en"
    ? ["What's the price?", "I run a salon / spa", "Does it work on WhatsApp?", "Start free 14 days"]
    : ["¿Cuánto cuesta?", "Tengo un salón / spa", "¿Funciona en WhatsApp?", "Empezar prueba gratis"];

  // Intro chips (first turn): identify if user is new or existing customer.
  const introChips = lang === "en"
    ? [
        { label: "I'm new here", payload: "I'm new, I don't have an account yet." },
        { label: "I have an account", payload: "Yes, I'm a customer, I have an account." },
      ]
    : [
        { label: "Soy nuevo", payload: "Soy nuevo, no tengo cuenta todavía." },
        { label: "Ya tengo cuenta", payload: "Sí, soy cliente, ya tengo cuenta." },
      ];

  const planBadgeColor = (plan) => {
    if (plan === "ai") return "bg-purple-500/20 text-purple-300 border-purple-500/40";
    if (plan === "pro") return "bg-[#FF7A00]/20 text-[#FF7A00] border-[#FF7A00]/40";
    return "bg-zinc-700/40 text-zinc-300 border-zinc-700";
  };

  return (
    <>
      {/* Floating launcher — mini Sofia robot evolved from Commandix logo */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] group"
          data-testid="sofia-launcher"
          aria-label={lang === "en" ? "Open chat with Sofia" : "Abrir chat con Sofia"}
        >
          {/* Outer ping ring */}
          <span className="absolute inset-0 rounded-full bg-[#FF7A00] opacity-25 animate-ping" style={{ animationDuration: "2.4s" }} />
          {/* Robot inside a glowing pill */}
          <span className="relative flex items-center justify-center w-[68px] h-[68px] rounded-full bg-[#0a0a0a] border-2 border-[#FF7A00]/50 animate-sofia-launcher-glow group-hover:scale-110 transition-transform duration-300">
            <SofiaRobot size={56} state="idle" />
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-[#0a0a0a] animate-status-pulse" />
          </span>
          {/* Hover label */}
          <span className="absolute right-[78px] top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-[#0a0a0a] border border-[#FF7A00]/40 text-white text-[11px] font-semibold opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-300 pointer-events-none shadow-[0_8px_24px_rgba(255,122,0,0.25)]">
            {lang === "en" ? "Chat with Sofia" : "Habla con Sofia"}
          </span>
        </button>
      )}

      {/* Chat panel — round, real-chat look */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-[60] w-[min(380px,calc(100vw-1.5rem))] h-[580px] max-h-[calc(100vh-2rem)] flex flex-col animate-sofia-panel-in"
          data-testid="sofia-panel"
        >
          {/* Avatar overflowing the panel top — Sofia robot evolved from Commandix logo */}
          <div className="absolute -top-9 left-1/2 -translate-x-1/2 z-10">
            <SofiaRobot size={84} state={botState} />
          </div>

          <div className="flex-1 bg-[#0a0a0a] border border-[#27272a] rounded-[28px] shadow-[0_24px_80px_rgba(255,122,0,0.22),0_8px_30px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden">
            {/* Header with animated aurora gradient */}
            <div className="px-5 pt-12 pb-3 border-b border-[#1f1f23] text-center relative">
              <div className="absolute inset-0 animate-sofia-aurora pointer-events-none" />
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 text-zinc-400 hover:text-white p-1.5 rounded-full hover:bg-white/5 transition z-10"
                data-testid="sofia-close"
                aria-label="Close"
              >
                <X size={16} />
              </button>
              <p className="relative text-[16px] font-extrabold text-white tracking-tight leading-none">Sofia</p>
              <p className="relative text-[10px] uppercase tracking-[0.2em] text-[#FF7A00] font-bold mt-1.5 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-status-pulse" />
                {mode === "authenticated"
                  ? (lang === "en" ? "Personal mode · Verified" : "Modo personal · Verificada")
                  : (lang === "en" ? "Online · replies in seconds" : "En línea · responde al instante")}
              </p>

              {mode === "authenticated" && authedUser && (
                <div className="relative mt-2.5 flex items-center justify-center gap-2 text-[11px]" data-testid="sofia-authed-badge">
                  <span className="inline-flex items-center gap-1 text-emerald-300">
                    <ShieldCheck size={12} weight="fill" /> {authedUser.name?.split(" ")[0] || "—"}
                  </span>
                  <span className="text-zinc-600">·</span>
                  <span className={`px-1.5 py-0.5 rounded-full border text-[9px] uppercase tracking-wider font-bold ${planBadgeColor(authedUser.plan)}`}>
                    {authedUser.plan_name}
                  </span>
                  <button onClick={logout} className="ml-1 text-zinc-500 hover:text-zinc-200" data-testid="sofia-logout">
                    <SignOut size={12} />
                  </button>
                </div>
              )}
              {mode === "locked" && (
                <div className="relative mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-bold">
                  <Lock size={10} weight="fill" /> {lang === "en" ? "Verification locked" : "Verificación bloqueada"}
                </div>
              )}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="sofia-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-sofia-bubble`}>
                  {m.role === "quote" ? (
                    <SofiaQuoteCard
                      quote={m.quote}
                      lang={lang}
                      onStart={() => {
                        const q = m.quote || {};
                        const params = new URLSearchParams({
                          plan: "custom",
                          price: String(q.price_usd_monthly || 150),
                          type: q.business_type || "",
                          summary: (q.business_summary || "").slice(0, 200),
                          modules: (Array.isArray(q.modules) ? q.modules : []).join(","),
                        });
                        navigate(`/signup?${params.toString()}`);
                      }}
                    />
                  ) : (
                    <div
                      className={`max-w-[82%] px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-gradient-to-br from-[#FFB266] via-[#FF7A00] to-[#FF3D00] text-black font-medium rounded-3xl rounded-br-md shadow-[0_4px_14px_rgba(255,122,0,0.35)]"
                          : "bg-gradient-to-br from-[#1a1a1d] to-[#141416] border border-[#27272a] text-zinc-100 rounded-3xl rounded-bl-md shadow-[0_4px_14px_rgba(0,0,0,0.4)]"
                      }`}
                    >
                      {m.text}
                    </div>
                  )}
                </div>
              ))}
              {sending && (
                <div className="flex justify-start animate-sofia-bubble">
                  <div className="bg-gradient-to-br from-[#1a1a1d] to-[#141416] border border-[#27272a] rounded-3xl rounded-bl-md px-4 py-3 shadow-[0_4px_14px_rgba(0,0,0,0.4)]">
                    <div className="flex items-center gap-1.5">
                      <span className="sofia-typing-dot w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                      <span className="sofia-typing-dot w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                      <span className="sofia-typing-dot w-1.5 h-1.5 rounded-full bg-[#FF7A00]" />
                    </div>
                  </div>
                </div>
              )}

              {/* Intro chips (first turn) — identify user type */}
              {messages.length === 1 && messages[0]?.showIntroChips && !sending && mode === "anon" && (
                <div className="flex flex-wrap gap-2 pt-2" data-testid="sofia-intro-chips">
                  {introChips.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendChat(q.payload)}
                      className="text-[12px] px-4 py-2 rounded-full bg-gradient-to-br from-[#FF7A00]/15 to-[#FF3D00]/10 border border-[#FF7A00]/40 text-white font-medium hover:border-[#FF7A00] hover:from-[#FF7A00]/25 transition shadow-[0_4px_14px_rgba(255,122,0,0.2)]"
                      data-testid={`sofia-intro-chip-${i}`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Sales quick replies — only after the intro turn while anon */}
              {messages.length > 1 && messages.length <= 3 && !sending && mode === "anon" && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {quickReplies.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendChat(q)}
                      className="text-[11px] px-3 py-1.5 rounded-full bg-[#18181b] border border-[#27272a] text-zinc-300 hover:border-[#FF7A00] hover:text-white transition"
                      data-testid={`sofia-quick-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* In-chat upgrade card (PayPal embedded) */}
              {upgradeCta && mode === "authenticated" && (
                <div
                  className="mt-2 p-4 rounded-2xl border border-[#FF7A00]/40 bg-gradient-to-br from-[#FF7A00]/10 via-[#FF3D00]/5 to-transparent"
                  data-testid="sofia-upgrade-card"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#FF7A00] font-bold flex items-center gap-1">
                        <Lightning size={11} weight="fill" /> {lang === "en" ? "Upgrade in 1 click" : "Activa en 1 clic"}
                      </p>
                      <p className="text-base font-extrabold text-white mt-0.5">Plan {upgradeCta.plan_name}</p>
                      <p className="text-[11px] text-zinc-400">${upgradeCta.price_usd}/mes · {lang === "en" ? "cancel anytime" : "cancela cuando quieras"}</p>
                    </div>
                    <button onClick={() => { setUpgradeCta(null); setUpgradeStatus("idle"); }} className="text-zinc-500 hover:text-white" data-testid="sofia-upgrade-close">
                      <X size={14} />
                    </button>
                  </div>

                  {upgradeStatus === "success" ? (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs">
                      <CheckCircle size={16} weight="fill" />
                      <span>{lang === "en" ? "Plan activated! See you in your dashboard." : "¡Plan activado! Ya puedes verlo en tu dashboard."}</span>
                    </div>
                  ) : upgradeStatus === "error" ? (
                    <div className="text-xs text-red-400 mb-2">
                      {lang === "en" ? "Payment failed. Try again or use /billing." : "El pago falló. Intenta de nuevo o ve a /billing."}
                    </div>
                  ) : (
                    <div className="rounded-lg overflow-hidden bg-white" data-testid="sofia-upgrade-paypal-wrap">
                      <PayPalScriptProvider options={{ "client-id": PAYPAL_CLIENT_ID, currency: "USD", intent: "capture" }}>
                        <PayPalButtons
                          style={{ layout: "horizontal", color: "gold", shape: "pill", label: "pay", height: 38, tagline: false }}
                          createOrder={async () => {
                            setUpgradeStatus("paying");
                            const { data } = await axios.post(`${API}/saas/sofia/checkout/start`, { session_id: sessionId, plan: upgradeCta.plan });
                            return data.order_id;
                          }}
                          onApprove={async (data) => {
                            try {
                              await axios.post(`${API}/saas/sofia/checkout/capture`, { session_id: sessionId, order_id: data.orderID, plan: upgradeCta.plan });
                              setUpgradeStatus("success");
                              setMessages((prev) => [...prev, {
                                role: "assistant",
                                text: lang === "en"
                                  ? `Boom 💥 plan ${upgradeCta.plan_name} active. Anything else I can help you with?`
                                  : `Boom 💥 plan ${upgradeCta.plan_name} activado. ¿Algo más en lo que te ayude?`,
                              }]);
                            } catch (e) {
                              setUpgradeStatus("error");
                            }
                          }}
                          onError={() => setUpgradeStatus("error")}
                          onCancel={() => setUpgradeStatus("idle")}
                        />
                      </PayPalScriptProvider>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Composer — switches into PIN entry when needed */}
            <div className="p-3 border-t border-[#1f1f23] bg-[#0a0a0a]">
              {mode === "awaiting_pin" ? (
                <div data-testid="sofia-pin-form">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#FF7A00] font-bold flex items-center gap-1.5 mb-2 px-1">
                    <EnvelopeSimple size={11} weight="bold" />
                    {lang === "en" ? "Enter PIN sent to" : "PIN enviado a"} {maskedEmail}
                  </p>
                  <div className="flex justify-between gap-1.5" onPaste={handlePinPaste}>
                    {pinDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (pinRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={(e) => handlePinChange(i, e.target.value)}
                        onKeyDown={(e) => handlePinKey(i, e)}
                        className="w-10 h-12 text-center text-lg font-extrabold bg-[#18181b] border border-[#27272a] rounded-xl text-white focus:border-[#FF7A00] focus:ring-2 focus:ring-[#FF7A00]/30 outline-none font-mono"
                        data-testid={`sofia-pin-${i}`}
                        disabled={sending}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2 px-1">
                    {attemptsLeft !== null ? (
                      <p className="text-[10px] text-amber-400">
                        {lang === "en"
                          ? `${attemptsLeft} attempt${attemptsLeft === 1 ? "" : "s"} left`
                          : `Te queda${attemptsLeft === 1 ? "" : "n"} ${attemptsLeft} intento${attemptsLeft === 1 ? "" : "s"}`}
                      </p>
                    ) : <span />}
                    <button onClick={() => setMode("anon")} className="text-[10px] text-zinc-500 hover:text-zinc-300">
                      {lang === "en" ? "Cancel" : "Cancelar"}
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); sendChat(); }} data-testid="sofia-input-form">
                  <div className="flex items-center gap-2 bg-[#18181b]/90 backdrop-blur-md border border-[#27272a] rounded-full pl-4 pr-1.5 py-1.5 focus-within:border-[#FF7A00] focus-within:shadow-[0_0_0_3px_rgba(255,122,0,0.18)] transition-all duration-300">
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={
                        mode === "awaiting_email"
                          ? (lang === "en" ? "Type your email…" : "Escribe tu email…")
                          : mode === "authenticated"
                          ? (lang === "en" ? `Ask Sofia about ${authedUser?.business_name || "your account"}…` : `Pregunta a Sofia sobre ${authedUser?.business_name || "tu cuenta"}…`)
                          : (lang === "en" ? "Type your question…" : "Escribe tu pregunta…")
                      }
                      className="flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 outline-none"
                      disabled={sending || mode === "locked"}
                      data-testid="sofia-input"
                    />
                    <button
                      type="submit"
                      disabled={!input.trim() || sending || mode === "locked"}
                      className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFB266] via-[#FF7A00] to-[#FF3D00] hover:scale-110 disabled:opacity-40 disabled:hover:scale-100 text-black flex items-center justify-center shadow-[0_4px_14px_rgba(255,122,0,0.4)] transition-transform duration-200"
                      data-testid="sofia-send"
                    >
                      <PaperPlaneRight size={16} weight="fill" />
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-600 text-center mt-2">
                    {mode === "authenticated" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400/80">
                        <CheckCircle size={10} weight="fill" /> {lang === "en" ? "Encrypted personal session" : "Sesión personal cifrada"}
                      </span>
                    ) : (
                      lang === "en" ? "Powered by AI · Don't share sensitive info" : "Impulsado por IA · No compartas información sensible"
                    )}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ====================================================================
// SofiaQuoteCard — rendered when Sofia emits a <<QUOTE>> block at the end
// of a diagnosis. Shows modules, complexity, and the personalized monthly
// price ($100–$200). Click "Empezar" routes the visitor to a custom signup.
// ====================================================================
function SofiaQuoteCard({ quote, lang, onStart }) {
  const tt = (en, es) => (lang === "en" ? en : es);
  const modules = Array.isArray(quote?.modules) ? quote.modules : [];
  const painPoints = Array.isArray(quote?.pain_points) ? quote.pain_points : [];
  const price = Number(quote?.price_usd_monthly || 0);
  const complexity = Math.max(1, Math.min(5, Number(quote?.complexity || 1)));
  return (
    <div
      className="max-w-[92%] w-full rounded-3xl rounded-bl-md bg-gradient-to-br from-[#FF7A00]/10 via-[#1a1a1d] to-[#141416] border border-[#FF7A00]/40 shadow-[0_8px_32px_rgba(255,122,0,0.25)] overflow-hidden"
      data-testid="sofia-quote-card"
    >
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-[#FF7A00]/20 to-transparent border-b border-[#FF7A00]/20">
        <div className="flex items-center gap-2 mb-1">
          <Sparkle size={14} weight="fill" className="text-[#FF7A00]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#FF7A00] font-bold">
            {tt("Personalized quote", "Cotización personalizada")}
          </span>
        </div>
        <p className="text-[13px] font-bold text-white leading-snug">{quote?.business_type || tt("Your business", "Tu negocio")}</p>
        {quote?.business_summary && (
          <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{quote.business_summary}</p>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {painPoints.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mb-1.5">
              {tt("Pain points", "Puntos de dolor")}
            </p>
            <ul className="space-y-1">
              {painPoints.map((p, i) => (
                <li key={i} className="text-[12px] text-zinc-300 flex items-start gap-1.5">
                  <span className="text-red-400 mt-0.5">•</span> {p}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mb-1.5">
            {tt("Recommended modules", "Módulos recomendados")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {modules.map((m, i) => (
              <span
                key={i}
                className="text-[11px] px-2.5 py-1 rounded-full bg-[#FF7A00]/15 text-[#FFB266] border border-[#FF7A00]/30"
              >
                ✦ {m}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mb-0.5">
              {tt("Complexity", "Complejidad")}
            </p>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <span
                  key={n}
                  className={`h-1.5 w-5 rounded-full ${n <= complexity ? "bg-[#FF7A00]" : "bg-zinc-800"}`}
                />
              ))}
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mb-0.5">
              {tt("Estimated", "Estimado")}
            </p>
            <p className="text-2xl font-extrabold text-[#FF7A00]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              ${price}
              <span className="text-xs text-zinc-500 font-normal">/{tt("mo", "mes")}</span>
            </p>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-4 py-3 bg-black/30 border-t border-[#FF7A00]/20">
        <button
          onClick={onStart}
          className="w-full h-10 rounded-xl bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold text-sm transition shadow-[0_4px_14px_rgba(255,122,0,0.35)]"
          data-testid="sofia-quote-start-btn"
        >
          {tt("Start with this plan", "Empezar con este plan")} →
        </button>
        <p className="text-[10px] text-center text-zinc-500 mt-2">
          {tt("14-day free trial · No card", "14 días gratis · Sin tarjeta")}
        </p>
      </div>
    </div>
  );
}
